import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { bateriaApi, clinicalApi } from "../../api/endpoints.js";
import { ApiError } from "../../api/client.js";
import type { CorregirItemInput, ItemRevision } from "../../api/types.js";
import { ScreenHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { SharedAudio } from "../../components/AudioPlayer.js";
import { Loader } from "../../components/Loader.js";
import { QaBanner } from "../../components/QaBanner.js";
import { ErrorBanner, InfoBanner, Field, Select } from "../../components/Form.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import { useSignedAudioPlayer } from "../../hooks/useSignedAudioPlayer.js";
import { useT, type Translate } from "../../i18n/index.js";
import styles from "./Workbench.module.css";

/** Lista estática de códigos PEFF (procesos E/A/S) para el corrector clínico.
 *  Reemplazable por un catálogo servido por el backend en una fase posterior. */
const CODIGOS_PEFF: string[] = [
  ...Array.from({ length: 8 }, (_, i) => `E${i + 1}`),
  ...Array.from({ length: 10 }, (_, i) => `A${i + 1}`),
  ...Array.from({ length: 16 }, (_, i) => `S${i + 1}`),
];

interface FormCorreccion {
  codigoOriginal: string;
  codigoCorregido: string;
  transcripcionCorregida: string;
  comentario: string;
}

function formVacio(item: ItemRevision): FormCorreccion {
  const c = item.correccion;
  return {
    codigoOriginal: c?.codigoOriginal ?? item.codigosMotor[0] ?? "",
    codigoCorregido: c?.codigoCorregido ?? "",
    transcripcionCorregida: c?.transcripcionCorregida ?? "",
    comentario: c?.comentario ?? "",
  };
}

/** Convierte el formulario a input, omitiendo vacíos (exactOptionalPropertyTypes). */
function aInput(f: FormCorreccion): CorregirItemInput {
  return {
    ...(f.codigoOriginal ? { codigoOriginal: f.codigoOriginal } : {}),
    ...(f.codigoCorregido ? { codigoCorregido: f.codigoCorregido } : {}),
    ...(f.transcripcionCorregida ? { transcripcionCorregida: f.transcripcionCorregida } : {}),
    ...(f.comentario ? { comentario: f.comentario } : {}),
  };
}

/** Un ítem "necesita oído humano" si tiene alguna marca clínica relevante. */
function tieneFlags(item: ItemRevision): boolean {
  const f = item.flags;
  return f.revisarAudio || f.dudoso || f.noEvoca || f.withHint || f.pendienteValidacionClinica;
}

function Flags({ item, t }: { item: ItemRevision; t: Translate }) {
  const f = item.flags;
  return (
    <>
      {f.withHint ? <span className={styles.flag}>{t("workbench.flag.withHint")}</span> : null}
      {f.noEvoca ? <span className={styles.flag}>{t("workbench.flag.noEvoca")}</span> : null}
      {f.revisarAudio ? (
        <span className={`${styles.flag} ${styles.flagAlert}`}>{t("workbench.flag.revisarAudio")}</span>
      ) : null}
      {f.dudoso ? <span className={styles.flag}>{t("workbench.flag.dudoso")}</span> : null}
      {f.pendienteValidacionClinica ? (
        <span className={`${styles.flag} ${styles.flagAlert}`}>{t("workbench.flag.pendiente")}</span>
      ) : null}
    </>
  );
}

export function WorkbenchPage() {
  const t = useT();
  const { bateriaId = "" } = useParams();
  const { data, loading, error, reload } = useQuery(
    () => clinicalApi.workbench(bateriaId),
    [bateriaId],
  );
  // C) Informe (variante terapeuta) + PeffReport crudo, lecturas independientes:
  // si fallan, la revisión ítem a ítem sigue funcionando.
  const informe = useQuery(() => clinicalApi.informe(bateriaId, "terapeuta"), [bateriaId]);
  const detalle = useQuery(() => bateriaApi.obtener(bateriaId), [bateriaId]);

  const items = useMemo(() => data?.items ?? [], [data]);
  const firmada = !!data?.validacion?.firmadaEn;

  // ---- corrector por ítem ---------------------------------------------------
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [form, setForm] = useState<FormCorreccion | null>(null);
  const [guardandoCorr, setGuardandoCorr] = useState(false);
  const [corrError, setCorrError] = useState<string | null>(null);

  function abrirEditor(item: ItemRevision) {
    setCorrError(null);
    setEditandoId(item.itemId);
    setForm(formVacio(item));
  }

  async function guardarCorreccion(itemId: string) {
    if (!form) return;
    setGuardandoCorr(true);
    setCorrError(null);
    try {
      await clinicalApi.corregir(bateriaId, itemId, aInput(form));
      setEditandoId(null);
      setForm(null);
      reload();
    } catch (e) {
      setCorrError(mensajeDeError(e));
    } finally {
      setGuardandoCorr(false);
    }
  }

  // ---- revisión ítem a ítem: filtro + player unificado ---------------------
  const [soloFlags, setSoloFlags] = useState(false);
  const [continua, setContinua] = useState(true);
  const [audioAviso, setAudioAviso] = useState<string | null>(null);

  const visibles = useMemo(() => (soloFlags ? items.filter(tieneFlags) : items), [items, soloFlags]);
  const conFlags = useMemo(() => items.filter(tieneFlags).length, [items]);

  // ¿Hay una corrección editada y SIN guardar? (para no perderla en un refresh)
  const itemEnEdicion = editandoId ? items.find((i) => i.itemId === editandoId) : undefined;
  const correccionSucia =
    !!itemEnEdicion && !!form && JSON.stringify(form) !== JSON.stringify(formVacio(itemEnEdicion));

  // Al expirar una URL firmada: si NO hay corrección en curso, recargamos (re-firma
  // todas las URLs, preserva el foco). Con una edición sin guardar NUNCA recargamos
  // en silencio: avisamos y dejamos el refresco a decisión del terapeuta.
  function alExpirarAudio() {
    if (correccionSucia) {
      setAudioAviso(t("workbench.audio.expiredDirty"));
      return;
    }
    setAudioAviso(t("workbench.audio.refreshing"));
    reload();
  }

  // Player unificado (un solo <audio> por foco). autoAdvance = "continua". El hook
  // acota las re-firmas (guard por-ítem + tope global) para que un audio corrupto
  // no dispare el loop de recarga; los ítems irrecuperables quedan noReproducibles.
  const audioItems = useMemo(
    () => visibles.map((v) => ({ id: v.itemId, audioUrl: v.audioUrl ?? null })),
    [visibles],
  );
  const player = useSignedAudioPlayer({
    items: audioItems,
    autoAdvance: continua,
    onExpired: alExpirarAudio,
  });
  const foco = visibles[player.idx];
  const focoNoReproducible = !!foco && player.noReproducibles.has(foco.itemId);

  // Al cambiar el filtro, vuelve al primer ítem visible.
  useEffect(() => {
    player.irA(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soloFlags]);

  // Refrescada la data (reload), las URLs son nuevas: se limpia el aviso de expiración.
  useEffect(() => {
    setAudioAviso(null);
  }, [data]);

  // ---- validación / firma ---------------------------------------------------
  const [observaciones, setObservaciones] = useState("");
  const [conclusion, setConclusion] = useState("");
  const [valMsg, setValMsg] = useState<string | null>(null);
  const [valError, setValError] = useState<string | null>(null);
  const [necesitaPerfil, setNecesitaPerfil] = useState(false);
  const [guardandoVal, setGuardandoVal] = useState(false);
  const [firmando, setFirmando] = useState(false);

  // Sembramos los campos del borrador con la validación existente al cargar.
  useEffect(() => {
    if (data?.validacion) {
      setObservaciones(data.validacion.observaciones ?? "");
      setConclusion(data.validacion.conclusionProfesional ?? "");
    }
  }, [data?.validacion?.id, data?.validacion?.updatedAt]);

  async function guardarBorrador() {
    setGuardandoVal(true);
    setValMsg(null);
    setValError(null);
    setNecesitaPerfil(false);
    try {
      await clinicalApi.guardarValidacion(bateriaId, {
        ...(observaciones ? { observaciones } : {}),
        ...(conclusion ? { conclusionProfesional: conclusion } : {}),
      });
      setValMsg(t("workbench.validacion.draftSaved"));
    } catch (e) {
      setValError(mensajeDeError(e));
    } finally {
      setGuardandoVal(false);
    }
  }

  async function firmar() {
    setFirmando(true);
    setValMsg(null);
    setValError(null);
    setNecesitaPerfil(false);
    try {
      // El backend firma con la validación ya persistida; guardamos el borrador
      // primero para no perder observaciones/conclusión escritas recién.
      await clinicalApi.guardarValidacion(bateriaId, {
        ...(observaciones ? { observaciones } : {}),
        ...(conclusion ? { conclusionProfesional: conclusion } : {}),
      });
      await clinicalApi.firmar(bateriaId);
      reload();
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setNecesitaPerfil(true);
      setValError(mensajeDeError(e));
    } finally {
      setFirmando(false);
    }
  }

  if (loading) {
    return (
      <>
        <ScreenHeader title={t("workbench.title")} back="/terapeuta/evaluaciones" />
        <Loader label={t("common.loading")} />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <ScreenHeader title={t("workbench.title")} back="/terapeuta/evaluaciones" />
        <Content>
          <ErrorBanner message={error ?? t("common.error")} />
          <Button variant="ghost" onClick={reload}>
            {t("common.retry")}
          </Button>
        </Content>
      </>
    );
  }

  return (
    <>
      <ScreenHeader title={t("workbench.title")} back="/terapeuta/evaluaciones" />
      <Content>
        {data.modoPrueba ? <QaBanner /> : null}
        <Card>
          <h2>{t("workbench.child", { id: data.childId.slice(0, 8) })}</h2>
          <p style={{ marginTop: 4 }}>
            {t("workbench.profile", { profile: data.profile ?? "—" })}
          </p>
          <p className={styles.trace} style={{ marginTop: 6 }}>
            {t("workbench.trace", {
              engine: data.engineVersion ?? "—",
              scoring: data.scoringVersion ?? "—",
            })}
          </p>
        </Card>

        {/* ---- revisión ítem a ítem (player único dirigido por el foco) ---- */}
        <Card>
          <div className={styles.focusNav} style={{ marginBottom: 8 }}>
            <h2 style={{ margin: 0 }}>{t("workbench.focusTitle")}</h2>
            <span className={styles.focusMeta} />
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={soloFlags}
                disabled={conFlags === 0}
                onChange={(e) => setSoloFlags(e.target.checked)}
              />
              {t("workbench.onlyFlags", { n: conFlags })}
            </label>
            <label className={styles.toggle}>
              <input type="checkbox" checked={continua} onChange={(e) => setContinua(e.target.checked)} />
              {t("workbench.continuous")}
            </label>
          </div>

          {foco ? (
            <div className={styles.focus} tabIndex={0} onKeyDown={player.onKeyDown}>
              <div className={styles.focusNav}>
                <Button
                  variant="ghost"
                  disabled={player.idx <= 0}
                  onClick={() => player.irA(player.idx - 1, false)}
                >
                  {t("workbench.prev")}
                </Button>
                <span className={styles.focusMeta}>
                  {t("workbench.item", { n: player.idx + 1, total: visibles.length })}
                  {player.reproducidos.has(foco.itemId) ? " · ✓" : ""}
                </span>
                <Button
                  variant="ghost"
                  disabled={player.idx >= visibles.length - 1}
                  onClick={() => player.irA(player.idx + 1, false)}
                >
                  {t("workbench.next")}
                </Button>
              </div>
              <dl className={styles.kv}>
                <dt>{t("workbench.col.ejercicio")}</dt>
                <dd>{foco.ejercicioCodigo}</dd>
                <dt>{t("workbench.col.estimulo")}</dt>
                <dd>{foco.estimulo}</dd>
                <dt>{t("workbench.col.objetivo")}</dt>
                <dd>{foco.targetPhonemes.join(" ")}</dd>
                <dt>{t("workbench.col.producido")}</dt>
                <dd>{foco.fonemasProducidos.join(" ") || "—"}</dd>
              </dl>
              <div>
                {foco.codigosMotor.map((c, i) => (
                  <span key={`${c}-${i}`} className={styles.chip}>
                    {c}
                  </span>
                ))}
                <Flags item={foco} t={t} />
              </div>
              {foco.audioUrl && !focoNoReproducible ? (
                <SharedAudio player={player} controls />
              ) : (
                <InfoBanner tone="warn">
                  {focoNoReproducible ? t("workbench.audio.noReproducible") : t("workbench.noAudio")}
                </InfoBanner>
              )}
              {audioAviso ? (
                <div className={styles.trace} style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
                  <span>{audioAviso}</span>
                  {correccionSucia ? (
                    <Button variant="ghost" onClick={reload}>
                      {t("workbench.audio.refreshNow")}
                    </Button>
                  ) : null}
                </div>
              ) : null}
              <p className={styles.trace} style={{ marginTop: 6 }}>
                {t("workbench.kbdHint")}
              </p>
            </div>
          ) : (
            <InfoBanner>{soloFlags ? t("workbench.noFlagged") : t("workbench.noItems")}</InfoBanner>
          )}
        </Card>

        {/* ---- tabla completa ---- */}
        {items.length > 0 ? (
          <Card>
            <h2>{t("workbench.tableTitle")}</h2>
            {corrError ? <ErrorBanner message={corrError} /> : null}
            <div className={styles.tableWrap} style={{ marginTop: 10 }}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t("workbench.col.ejercicio")}</th>
                    <th>{t("workbench.col.estimulo")}</th>
                    <th>{t("workbench.col.objetivo")}</th>
                    <th>{t("workbench.col.producido")}</th>
                    <th>{t("workbench.col.codigos")}</th>
                    <th>{t("workbench.col.estados")}</th>
                    <th>{t("workbench.col.flags")}</th>
                    <th>{t("workbench.col.audio")}</th>
                    <th>{t("workbench.col.accion")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((item) => {
                    const editando = editandoId === item.itemId;
                    const corregido = !!item.correccion;
                    const enFoco = item.itemId === foco?.itemId;
                    return (
                      <tr
                        key={item.itemId}
                        className={[corregido ? styles.corrected : "", enFoco ? styles.focoRow : ""]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <td style={{ fontWeight: 700 }}>{item.ejercicioCodigo}</td>
                        <td>{item.estimulo}</td>
                        <td>{item.targetPhonemes.join(" ")}</td>
                        <td>{item.fonemasProducidos.join(" ") || "—"}</td>
                        <td style={{ whiteSpace: "normal" }}>
                          {(item.correccion?.codigoCorregido
                            ? [item.correccion.codigoCorregido]
                            : item.codigosMotor
                          ).map((c, j) => (
                            <span key={`${c}-${j}`} className={styles.chip}>
                              {c}
                            </span>
                          ))}
                        </td>
                        <td style={{ whiteSpace: "normal" }}>
                          {item.estados.map((s, j) => (
                            <span key={`${s}-${j}`} className={styles.chip}>
                              {s}
                            </span>
                          ))}
                        </td>
                        <td style={{ whiteSpace: "normal" }}>
                          <Flags item={item} t={t} />
                        </td>
                        <td>
                          {!item.audioUrl ? (
                            "—"
                          ) : player.noReproducibles.has(item.itemId) ? (
                            <span className={`${styles.flag} ${styles.flagAlert}`} title={t("workbench.audio.noReproducible")}>
                              ⚠ {t("workbench.audio.noReproducibleShort")}
                            </span>
                          ) : (
                            <Button variant="ghost" onClick={() => player.irAItem(item.itemId, true)}>
                              {player.reproducidos.has(item.itemId) ? "✓ ▶" : "▶"}
                            </Button>
                          )}
                        </td>
                        <td>
                          {!firmada ? (
                            <Button
                              variant="ghost"
                              onClick={() => (editando ? setEditandoId(null) : abrirEditor(item))}
                            >
                              {t("workbench.correct")}
                            </Button>
                          ) : null}
                          {editando && form ? (
                            <div className={styles.editor}>
                              <Field label={t("workbench.correct.codigoOriginal")}>
                                <Select
                                  value={form.codigoOriginal}
                                  onChange={(e) =>
                                    setForm({ ...form, codigoOriginal: e.target.value })
                                  }
                                >
                                  <option value="">{t("workbench.correct.none")}</option>
                                  {CODIGOS_PEFF.map((c) => (
                                    <option key={c} value={c}>
                                      {c}
                                    </option>
                                  ))}
                                </Select>
                              </Field>
                              <Field label={t("workbench.correct.codigoCorregido")}>
                                <Select
                                  value={form.codigoCorregido}
                                  onChange={(e) =>
                                    setForm({ ...form, codigoCorregido: e.target.value })
                                  }
                                >
                                  <option value="">{t("workbench.correct.none")}</option>
                                  {CODIGOS_PEFF.map((c) => (
                                    <option key={c} value={c}>
                                      {c}
                                    </option>
                                  ))}
                                </Select>
                              </Field>
                              <Field label={t("workbench.correct.transcripcion")}>
                                <input
                                  className={styles.textarea}
                                  style={{ minHeight: "auto" }}
                                  value={form.transcripcionCorregida}
                                  onChange={(e) =>
                                    setForm({ ...form, transcripcionCorregida: e.target.value })
                                  }
                                />
                              </Field>
                              <Field label={t("workbench.correct.comentario")}>
                                <textarea
                                  className={styles.textarea}
                                  value={form.comentario}
                                  onChange={(e) => setForm({ ...form, comentario: e.target.value })}
                                />
                              </Field>
                              <Button
                                disabled={guardandoCorr}
                                onClick={() => void guardarCorreccion(item.itemId)}
                              >
                                {guardandoCorr ? t("common.saving") : t("workbench.correct.save")}
                              </Button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        ) : null}

        {/* ---- C) informe (variante terapeuta) + PeffReport crudo colapsables ---- */}
        <Card>
          <details>
            <summary style={{ cursor: "pointer", fontWeight: 700 }}>{t("workbench.report.informe")}</summary>
            <div style={{ marginTop: 8 }}>
              {informe.loading ? (
                <Loader label={t("common.loading")} />
              ) : informe.data?.recomendacion ? (
                <>
                  <p style={{ fontWeight: 700 }}>{informe.data.recomendacion.titulo}</p>
                  <p style={{ whiteSpace: "pre-line" }}>{informe.data.recomendacion.mensaje}</p>
                  {informe.data.conclusionProfesional ? (
                    <p style={{ whiteSpace: "pre-line" }}>{informe.data.conclusionProfesional}</p>
                  ) : null}
                  {informe.data.disclaimer ? (
                    <p className={styles.trace} style={{ marginTop: 6 }}>{informe.data.disclaimer}</p>
                  ) : null}
                </>
              ) : (
                <InfoBanner>{t("workbench.report.unavailable")}</InfoBanner>
              )}
            </div>
          </details>
          <details style={{ marginTop: 10 }}>
            <summary style={{ cursor: "pointer", fontWeight: 700 }}>{t("workbench.report.raw")}</summary>
            {detalle.data?.peffReport ? (
              <pre className={styles.raw}>{JSON.stringify(detalle.data.peffReport, null, 2)}</pre>
            ) : (
              <div style={{ marginTop: 8 }}>
                <InfoBanner>{t("workbench.report.unavailable")}</InfoBanner>
              </div>
            )}
          </details>
        </Card>

        {/* ---- validación / firma ---- */}
        <Card>
          <h2>{t("workbench.validacion.title")}</h2>
          {firmada && data.validacion ? (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
              <div className={styles.signed}>
                {t("workbench.validacion.signed", {
                  fecha: new Date(data.validacion.firmadaEn!).toLocaleDateString(),
                  nombre: data.validacion.nombreProfesional ?? "—",
                  colegiatura: data.validacion.numeroColegiatura ?? "—",
                })}
              </div>
              <InfoBanner>{t("workbench.validacion.signedHint")}</InfoBanner>
              {data.validacion.conclusionProfesional ? (
                <p style={{ whiteSpace: "pre-line" }}>{data.validacion.conclusionProfesional}</p>
              ) : null}
              {data.validacion.observaciones ? (
                <p style={{ whiteSpace: "pre-line", color: "var(--ink-soft)" }}>
                  {data.validacion.observaciones}
                </p>
              ) : null}
            </div>
          ) : (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
              <Field label={t("workbench.validacion.observaciones")}>
                <textarea
                  className={styles.textarea}
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                />
              </Field>
              <Field label={t("workbench.validacion.conclusion")}>
                <textarea
                  className={styles.textarea}
                  value={conclusion}
                  onChange={(e) => setConclusion(e.target.value)}
                />
              </Field>
              {valMsg ? <InfoBanner>{valMsg}</InfoBanner> : null}
              {valError ? <ErrorBanner message={valError} /> : null}
              {necesitaPerfil ? (
                <InfoBanner tone="warn">
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <span>{t("workbench.validacion.needProfile")}</span>
                    <Link to="/terapeuta/perfil">
                      <Button variant="sun">{t("workbench.validacion.toProfile")}</Button>
                    </Link>
                  </div>
                </InfoBanner>
              ) : null}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Button variant="ghost" disabled={guardandoVal} onClick={() => void guardarBorrador()}>
                  {guardandoVal ? t("common.saving") : t("workbench.validacion.draft")}
                </Button>
                <Button variant="primary" disabled={firmando} onClick={() => void firmar()}>
                  {firmando ? t("common.saving") : t("workbench.validacion.sign")}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </Content>
    </>
  );
}
